// StoragePriceConfig.js
import React, { useState, useEffect } from "react";
import {
  Form,
  InputNumber,
  Button,
  message,
  Typography,
  Spin,
  Grid,
  Table,
  Select,
} from "antd";
import axiosInstance from "../utils/axiosInstance";
import {
  EuroCircleOutlined,
  SaveOutlined,
  CloudServerOutlined,
  DatabaseOutlined,
} from "@ant-design/icons";

const { Text } = Typography;
const { useBreakpoint } = Grid;
const { Option } = Select;

export default function StoragePriceConfig() {
  const screens = useBreakpoint();
  const CATEGORY_NAME = "Beds";

  const [categoryId, setCategoryId] = useState(null);

  // Sale (EUR, unchanged) + Cost with currency (EUR/GBP)
  const [cloudBase, setCloudBase] = useState(0);
  const [cloudCost, setCloudCost] = useState(0);
  const [costCurrency, setCostCurrency] = useState("EUR"); // ← comes from backend

  // 💡 Multiplier semantics: EUR = GBP × costMultiplier
  // Default: 1 GBP = 0.80 EUR  → costMultiplier = 0.80
  const [costMultiplier, setCostMultiplier] = useState(0.80);

  const [sizes, setSizes] = useState([]);
  const [ottomanPrices, setOttomanPrices] = useState({});
  const [ottomanCosts, setOttomanCosts] = useState({}); // raw numbers as saved

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const filterOutQueenSize = (sizesArray) =>
    sizesArray.filter(
      (size) => (size.size || "").trim().toLowerCase() !== "queen"
    );

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);

        // 1) Category
        const { data: catData } = await axiosInstance.get(
          "/category/categories"
        );
        const bedsCat = catData.categories?.find(
          (c) => c.category_name === CATEGORY_NAME
        );
        if (!bedsCat)
          return message.error(`Category "${CATEGORY_NAME}" not found`);
        setCategoryId(bedsCat.id);

        // 2) Fetch in parallel
        const [cloudRes, sizesRes, pricesRes] = await Promise.all([
          axiosInstance.get(
            `/admin/storage-prices/cloud?categoryId=${bedsCat.id}`
          ),
          axiosInstance.get("/product/sizes"),
          axiosInstance.get(`/admin/storage-prices?categoryId=${bedsCat.id}`),
        ]);

        const rawSizes = Array.isArray(sizesRes.data.sizes)
          ? sizesRes.data.sizes
          : Array.isArray(sizesRes.data)
          ? sizesRes.data
          : [];
        const filteredSizes = filterOutQueenSize(rawSizes).sort(
          (a, b) => a.id - b.id
        );

        // Cloud (read exactly what backend sends)
        setCloudBase(cloudRes.data.cloudStorageBase ?? 0);
        setCloudCost(cloudRes.data.cloudCost ?? 0);
        setCostCurrency(cloudRes.data.costCurrency || "EUR");
        setCostMultiplier(
          typeof cloudRes.data.costMultiplier === "number" &&
            isFinite(cloudRes.data.costMultiplier)
            ? cloudRes.data.costMultiplier
            : 0.80
        );

        // Maps (keep keys as strings to be consistent)
        const toStringKeyed = (obj) =>
          Object.fromEntries(
            Object.entries(obj || {}).map(([k, v]) => [String(k), v ?? 0])
          );

        const ottMap = toStringKeyed(pricesRes.data.ottomanPrices);
        const costMap = toStringKeyed(pricesRes.data.ottomanCosts);

        const allowedIds = new Set(filteredSizes.map((s) => String(s.id)));
        setOttomanPrices(
          Object.fromEntries(
            Object.entries(ottMap).filter(([id]) =>
              allowedIds.has(String(id))
            )
          )
        );
        setOttomanCosts(
          Object.fromEntries(
            Object.entries(costMap).filter(([id]) =>
              allowedIds.has(String(id))
            )
          )
        );
        setSizes(filteredSizes);
      } catch (err) {
        console.error(err);
        message.error("Failed to load storage configuration");
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleOttomanPriceChange = (sizeId, val) => {
    const k = String(sizeId);
    setOttomanPrices((prev) => ({ ...prev, [k]: val == null ? 0 : val }));
  };

  const handleOttomanCostChange = (sizeId, val) => {
    const k = String(sizeId);
    setOttomanCosts((prev) => ({ ...prev, [k]: val == null ? 0 : val }));
  };

  // 🔁 convert all cost values when currency changes
  const handleCurrencyChange = (nextCurrency) => {
    setCostCurrency((prevCurrency) => {
      if (prevCurrency === nextCurrency) return prevCurrency;

      setCloudCost((prev) => {
        // GBP → EUR : multiply by 0.80
        if (nextCurrency === "EUR" && prevCurrency === "GBP") {
          return Number((Number(prev) * Number(costMultiplier)).toFixed(2));
        }
        // EUR → GBP : divide by 0.80
        if (nextCurrency === "GBP" && prevCurrency === "EUR") {
          return Number((Number(prev) / Number(costMultiplier)).toFixed(2));
        }
        return prev;
      });

      setOttomanCosts((prevMap) => {
        const out = { ...prevMap };
        // GBP → EUR
        if (nextCurrency === "EUR" && prevCurrency === "GBP") {
          Object.keys(out).forEach((k) => {
            out[k] = Number(
              (Number(out[k] || 0) * Number(costMultiplier)).toFixed(2)
            );
          });
        }
        // EUR → GBP
        if (nextCurrency === "GBP" && prevCurrency === "EUR") {
          Object.keys(out).forEach((k) => {
            out[k] = Number(
              (Number(out[k] || 0) / Number(costMultiplier)).toFixed(2)
            );
          });
        }
        return out;
      });

      return nextCurrency;
    });
  };

  // 🆕 when multiplier changes and we are viewing GBP,
  // rescale displayed GBP numbers by old/new (keeps the underlying EUR constant).
  const handleMultiplierChange = (next) => {
    const newVal =
      typeof next === "number" && isFinite(next) && next > 0 ? next : 0.8;

    setCostMultiplier((prevMul) => {
      if (costCurrency === "GBP" && prevMul && newVal && prevMul !== newVal) {
        // For EUR = GBP * m, and we display GBP = EUR / m
        // Keep EUR constant → GBP_new = GBP_old * (m_old / m_new)
        const ratio = Number(prevMul) / Number(newVal);

        setCloudCost((prev) =>
          Number((Number(prev || 0) * ratio).toFixed(2))
        );

        setOttomanCosts((prevMap) => {
          const out = { ...prevMap };
          Object.keys(out).forEach((k) => {
            out[k] = Number((Number(out[k] || 0) * ratio).toFixed(2));
          });
          return out;
        });
      }
      return newVal;
    });
  };

  const handleSave = async () => {
    if (categoryId == null) return message.error("Category not loaded");
    setSaving(true);
    try {
      await axiosInstance.post("/admin/storage-prices/cloud", {
        categoryId,
        cloudStorageBase: cloudBase, // EUR (sale)
        cloudCost: cloudCost,        // raw number in chosen currency
        costCurrency,                // "EUR" | "GBP"
        costMultiplier,              // e.g. 0.80
      });

      await axiosInstance.post("/admin/save-storage-prices", {
        categoryId,
        ottomanPrices,
        ottomanCosts,
        cloudBase,
        cloudCost,
        costCurrency,
        costMultiplier,
      });

      message.success("All prices saved");
    } catch (err) {
      console.error(err);
      message.error("Failed to save prices");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 flex items-center justify-center">
        <div className="text-center">
          <Spin size="large" />
          <p className="mt-4 text-gray-600">Loading storage prices...</p>
        </div>
      </div>
    );
  }

  const tableData = sizes.map((sz) => ({
    key: String(sz.id),
    sizeLabel: sz.size,
    ottomanPrice: ottomanPrices[String(sz.id)] ?? 0, // EUR
    ottomanCost: ottomanCosts[String(sz.id)] ?? 0,   // raw in current currency
  }));

  const currencySymbol = costCurrency === "GBP" ? "£" : "€";

  const columns = [
    {
      title: "Size",
      dataIndex: "sizeLabel",
      key: "size",
      width: 120,
      render: (text) => (
        <div className="font-semibold text-gray-900 text-center bg-gray-50 py-2 px-4 rounded-lg border">
          {text}
        </div>
      ),
    },
    {
      title: "Ottoman Sale Price (€)",
      dataIndex: "ottomanPrice",
      key: "price",
      render: (price, rec) => (
        <InputNumber
          min={0}
          value={price}
          onChange={(val) => handleOttomanPriceChange(rec.key, val)}
          className="w-full rounded-lg border-gray-300"
          disabled={saving}
          size="large"
          formatter={(value) =>
            `€ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
          }
          parser={(value) => value.replace(/€\s?|(,*)/g, "")}
        />
      ),
    },
    {
      title: (
        <div className="flex items-center gap-2">
          <span>Ottoman Cost Price</span>
          <Select
            size="small"
            value={costCurrency}
            onChange={handleCurrencyChange}
            style={{ width: 90 }}
          >
            <Option value="EUR">EUR</Option>
            <Option value="GBP">GBP</Option>
          </Select>
          {costCurrency === "GBP" && (
            <span className="flex items-center gap-1">
              <span className="text-gray-500 text-xs">×</span>
              <InputNumber
                size="small"
                min={0}
                step={0.01}
                value={costMultiplier}
                onChange={handleMultiplierChange}
                style={{ width: 80 }}
              />
            </span>
          )}
        </div>
      ),
      dataIndex: "ottomanCost",
      key: "cost",
      render: (cost, rec) => (
        <InputNumber
          min={0}
          value={cost}
          onChange={(val) => handleOttomanCostChange(rec.key, val)}
          className="w-full rounded-lg border-gray-300"
          disabled={saving}
          size="large"
          formatter={(value) =>
            `${currencySymbol} ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
          }
          parser={(value) => value.replace(/[£€]\s?|(,*)/g, "")}
        />
      ),
    },
  ];

  return (
    <Spin spinning={loading} size="large" className="min-h-screen">
      <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 py-6 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6 mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
                <EuroCircleOutlined className="text-white text-xl" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900 mb-2">
                  Storage Prices Configuration
                </h1>
                <p className="text-gray-600">
                  Cloud sale price (EUR) and ottoman costs with currency.
                </p>
              </div>
            </div>
          </div>

          {/* Cloud */}
          <div className="grid grid-cols-1 gap-6 mb-6">
            <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
              <div className="flex items-center gap-3 mb-6">
                <div className="w-12 h-12 bg-gradient-to-br from紫-500 to-pink-500 rounded-xl flex items-center justify-center shadow-lg">
                  <CloudServerOutlined className="text-white text-xl" />
                </div>
                <div>
                  <h3 className="text-xl font-semibold text-gray-900">
                    Cloud Storage
                  </h3>
                  <p className="text-sm text-gray-500">Base price for all sizes</p>
                </div>
              </div>

              <Form layout="vertical">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Form.Item
                    label={
                      <span className="font-medium text-gray-700 text-lg">
                        Sale Price (€)
                      </span>
                    }
                  >
                    <InputNumber
                      min={0}
                      value={cloudBase}
                      onChange={(v) => setCloudBase(v || 0)}
                      disabled={saving}
                      className="w-full rounded-xl h-14 text-xl font-semibold border-2 border-gray-200"
                      size="large"
                      formatter={(value) =>
                        `€ ${value}`.replace(/\B(?=(\d{3})+(?!\d))/g, ",")
                      }
                      parser={(value) => value.replace(/€\s?|(,*)/g, "")}
                    />
                  </Form.Item>

                  <Form.Item
                    label={
                      <div className="flex items-center gap-2">
                        <span className="font-medium text-gray-700 text-lg">
                          Cost Price
                        </span>
                        <Select
                          size="small"
                          value={costCurrency}
                          onChange={handleCurrencyChange}
                          style={{ width: 90 }}
                        >
                          <Option value="EUR">EUR</Option>
                          <Option value="GBP">GBP</Option>
                        </Select>
                        {costCurrency === "GBP" && (
                          <span className="flex items-center gap-1">
                            <span className="text-gray-500 text-xs">×</span>
                            <InputNumber
                              size="small"
                              min={0}
                              step={0.01}
                              value={costMultiplier}
                              onChange={handleMultiplierChange}
                              style={{ width: 80 }}
                            />
                          </span>
                        )}
                      </div>
                    }
                  >
                    <InputNumber
                      min={0}
                      value={cloudCost}
                      onChange={(v) => setCloudCost(v || 0)}
                      disabled={saving}
                      className="w-full rounded-xl h-14 text-xl font-semibold border-2 border-gray-200"
                      size="large"
                      formatter={(value) =>
                        `${costCurrency === "GBP" ? "£" : "€"} ${value}`.replace(
                          /\B(?=(\d{3})+(?!\d))/g,
                          ","
                        )
                      }
                      parser={(value) => value.replace(/[£€]\s?|(,*)/g, "")}
                    />
                  </Form.Item>
                </div>
              </Form>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 bg-orange-100 rounded-lg flex items-center justify-center">
                    <DatabaseOutlined className="text-orange-600 text-lg" />
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900">
                      Ottoman Storage Prices by Size
                    </h3>
                    <p className="text-sm text-gray-500">
                      Sale (EUR) and cost in chosen currency
                    </p>
                  </div>
                </div>
                <div className="bg-orange-50 border border-orange-200 rounded-lg px-4 py-2">
                  <div className="text-sm font-medium text-orange-700">
                    {sizes.length} sizes available
                  </div>
                </div>
              </div>
            </div>

            <div className="p-6">
              <Table
                dataSource={tableData}
                columns={columns}
                pagination={false}
                size="middle"
                className="modern-storage-table"
                rowClassName="hover:bg-blue-50 transition-colors duration-200"
              />

              <div className="mt-8 pt-6 border-t border-gray-200">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
                  <div className="flex-1">
                    <Text type="secondary" className="text-sm">
                      <strong>Note:</strong> Sale prices remain in EUR. Cost
                      prices are stored with the currency and multiplier you
                      select (EUR/GBP), and returned exactly as saved.
                    </Text>
                  </div>
                  <Button
                    type="primary"
                    icon={<SaveOutlined />}
                    onClick={handleSave}
                    loading={saving}
                    disabled={loading}
                    className="h-12 px-8 rounded-xl font-semibold"
                    size="large"
                  >
                    {saving ? "Saving..." : "Save All Prices"}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <style jsx>{`
        .modern-storage-table .ant-table-thead > tr > th {
          background: #f8fafc;
          font-weight: 600;
          border-bottom: 2px solid #e5e7eb;
          white-space: nowrap;
          padding: 16px;
          font-size: 14px;
        }
        .modern-storage-table .ant-table-tbody > tr > td {
          border-bottom: 1px solid #f1f5f9;
          padding: 16px;
        }
      `}</style>
    </Spin>
  );
}
